import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import EmptyMessage from '@/components/shared/EmptyMessage';
import ErrorMessage from '@/components/shared/ErrorMessage';
import InfinityList from '@/components/ui/custom/InfinityList';
import { MANAGEMENT_PAGE_SIZE, REQUEST_STALE_TIME_IN_MS } from '@/constants';
import useInfiniteService from '@/data/hook/useInfiniteService';
import { lessonBlocksQueryKeys } from '@/data/services/querykeys';
import Unit from '@/models/Unit';
import UnitListSkeleton from './UnitListSkeleton';

type UnitListProps = {
  slug?: string;
  isLoading?: boolean;
  children: (unit: Unit, index: number) => ReactNode;
};

export default function UnitList({ slug, isLoading, children }: UnitListProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLessons.unitList',
  });

  const {
    results: units,
    isInitialLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteService({
    ...lessonBlocksQueryKeys.list(slug ?? '', {
      pageSize: MANAGEMENT_PAGE_SIZE,
    })._ctx.infinity,
    enabled: !!slug,
    staleTime: REQUEST_STALE_TIME_IN_MS,
  });

  if (isLoading || isInitialLoading) return <UnitListSkeleton />;

  if (error)
    return <ErrorMessage text={t('loadError')} onRetry={() => refetch()} />;

  if (!slug) return null;

  if (!units.length) return <EmptyMessage text={t('empty')} />;

  return (
    <InfinityList
      className="h-auto gap-4"
      onReachEnd={fetchNextPage}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
    >
      {units.map(children)}
    </InfinityList>
  );
}
